import { getConfig } from '../config/config';
import { getKeycloakInstance } from '../config/keycloak';
import {
  APIKeyDetails,
  APIKeyInstancePayload,
  APIKeyPayload,
  CreateAPIKeyResponse,
  SearchAPIKeysResponse,
  UpdateAPIKeyPayload,
} from '../constants/types';
import { getTenant } from '../utils/getKeycloakConfig';
import makeRequest from './makeRequest';

const getAllApiKeys = (query: string) => {
  const allKeys: {
    status: string;
    data: {
      count: number;
      page: number;
      pageSize: number;
      result: APIKeyDetails[];
    };
  } = {
    status: 'success',
    data: { count: 0, page: 1, pageSize: 1000, result: [] },
  }; // this array will contain all employees
  let pageNumber = 1; // start with page 1
  const getThoseEmployees = async () => {
    const url = `${
      getConfig().utilitiesApiEndpoint
    }/${getTenant()}/api/v1/key/apikeys?_page=${pageNumber}&_size=1000&type=synthetic${query}`;
    const headers = {
      Authorization: `Bearer ${getKeycloakInstance()?.token}`,
    };

    const { data } = await makeRequest('GET', url, '', headers);
    allKeys.data.result = allKeys.data.result.concat(data.data.result);
    pageNumber += 1;
    if (data.data.result.length < 1000) {
      // this was the last page, return the collected contacts
      return allKeys;
    } else {
      // do me again...
      return getThoseEmployees();
    }
  };
  return getThoseEmployees();
};

const fetchApiKeys = async (query?: string): Promise<SearchAPIKeysResponse> => {
  const url = `${
    getConfig().utilitiesApiEndpoint
  }/${getTenant()}/api/v1/key/apikeys?_page=1&_size=1000&type=synthetic${query}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchAllAPIKeys = async () => {
  let allAPIKeys: APIKeyDetails[] = [];

  try {
    allAPIKeys = await Promise.all([
      getAllApiKeys('&status=active'),
      fetchApiKeys('&status=expired'),
      fetchApiKeys('&status=revoked'),
    ]).then(apiKeys => {
      return apiKeys.map(apiKey => apiKey.data.result).flat();
    });
  } catch (error: any) {
    allAPIKeys = [];
    throw error?.response;
  }

  return allAPIKeys;
};

const createSyntheticApi = async (
  payload: APIKeyPayload | APIKeyInstancePayload,
): Promise<CreateAPIKeyResponse> => {
  const url = `${
    getConfig().utilitiesApiEndpoint
  }/${getTenant()}/api/v1/key/apikeys/synthetic`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('POST', url, payload, headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const updateApiKey = async (
  id: string,
  payload: UpdateAPIKeyPayload,
): Promise<void> => {
  const url = `${
    getConfig().utilitiesApiEndpoint
  }/${getTenant()}/api/v1/key/apikeys/${id}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('PUT', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const revokeApiKey = async (id: string): Promise<void> => {
  const url = `${
    getConfig().utilitiesApiEndpoint
  }/${getTenant()}/api/v1/key/apikeys/${id}/revoke`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('PUT', url, '', headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const reactivateApiKey = async (
  id: string,
  payload: UpdateAPIKeyPayload,
): Promise<void> => {
  const url = `${
    getConfig().utilitiesApiEndpoint
  }/${getTenant()}/api/v1/key/apikeys/${id}/reactivate`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('PUT', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

export {
  fetchApiKeys,
  fetchAllAPIKeys,
  createSyntheticApi,
  revokeApiKey,
  reactivateApiKey,
  updateApiKey,
};
